import { Injectable } from '@angular/core';
import {
  defer,
  filter,
  finalize,
  first,
  lastValueFrom,
  Observable,
  Subject,
  Subscriber,
  takeUntil
} from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { v5 as uuidv5 } from 'uuid';
import { IPlugin, IUser, ModelService, PluginInjectorService } from 'core-client/api';
import { IApplicationPluginExtended, IPluginSettingsExtended } from './shared/interfaces/wms-chart.interface';
import { ISocketTokenAuth, WmsChartSocketIoService } from './shared/services/socket-io/socket-io.service';
import { PLUGIN_CONFIG_EVENTS, WMS_CHART_SERVER_EVENTS } from './shared/constants/event-constants';
import { PERMISSION_REF_WMS_CHART_CONSUMER } from './shared/constants/permission-constant';


/**
 * WMS Chart Loader Service
 *
 * - Manages WMS token caching and distribution
 * - Listens for token broadcasts from backend
 * - Delegates socket connection management to WmsChartSocketIoService
 * - Notifies SocketIoService about plugin enable/disable state
 * - Backend fetches new access token before expiry (client_credentials)
 * - Backend broadcasts token updates to all connected clients
 * - Service listens and updates cache + notifies all widget handlers
 * - All widgets share the same global token (single-tenant architecture)
 *
 * Invalid Token Feedback Loop:
 * - WMS tile requests failing with 401/403 → notifyInvalidToken()
 * - Clears local cache + notifies backend via socket event
 * - Backend immediately fetches new token + broadcasts to all clients
 * - Frontend receives new token via broadcast listener
 */
@Injectable({
  providedIn: 'root'
})
export class WmsChartService {
  private plugin: IApplicationPluginExtended = <IApplicationPluginExtended>{};
  private readonly pluginName: string = 'WMS_CHARTS';

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  private tokenBroadcastSubscription$: Subject<void> = new Subject<void>();

  // Widget token handlers (widgetId → callback)
  private readonly widgetTokenHandlers: Map<string, (token: string | null) => void> = new Map();

  // Global token cache (single-tenant: all widgets share same token)
  private tokenCache: string | null = null;

  // Track if token listener is active
  private isTokenListenerActive: boolean = false;

  // ✅ NEW: Prevent spamming backend when multiple tiles fail simultaneously
  private invalidTokenNotificationPending: boolean = false;

  constructor(
    public readonly socket: WmsChartSocketIoService,
    private readonly modelService: ModelService,
    private readonly pluginInjectorService: PluginInjectorService,
    private readonly snackBar: MatSnackBar,
    private readonly translator: TranslateService
  ) {}

  setup(): void {
    this.initPlugin();
    this.start();
  }

  private initPlugin(): void {
    this.plugin.name = this.pluginName;
    this.plugin.enabled = false;
    this.plugin.description = 'Plugin service that connects and load WMS TileLayers from a WMS Chart Server.';
    this.plugin.settings = {
      auth_url: '',
      client_id: '',
      client_secret: '',
      realm: '',
      wms_server_url: ''
    };
    this.plugin.version = '0.0.1';

    this.pluginInjectorService.register(this.plugin);
  }

  /**
   * Execute socket.emit with callback ack and return result as observable
   */
  private emitAck<T>(event: string, ...args: any[]): Observable<T> {
    return defer(() => new Observable<T>((observer: Subscriber<T>) => {
      this.socket.emit(event, ...args, (result: T) => {
        observer.next(result);
        observer.complete();
      });
    })).pipe(first());
  }

  /**
   * Start listening for token broadcasts from backend
   * Listens directly to socket events WITHOUT handler registration
   *
   * ONLY active when widgets exist - no unnecessary overhead
   */
  private listenForTokenUpdates(): void {
    console.log('[WMS Chart Loader] Starting token broadcast listener');

    this.socket.fromEvent('wms_token_updated')
      .pipe(takeUntil(this.tokenBroadcastSubscription$))
      .subscribe((data: unknown) => {
        const token = data as string;
        console.log(`[WMS Chart Loader] Token updated from backend`);

        // Update cached token
        this.tokenCache = token;

        // Notify all registered widget handlers
        this.widgetTokenHandlers.forEach((handler: (token: string | null) => void) => {
          handler(token);
        });
      });
  }

  /**
   * Stop listening for token broadcasts
   */
  private stopListeningForTokenUpdates(): void {
    console.log('[WMS Chart Loader] Stopping token broadcast listener');

    this.tokenBroadcastSubscription$.next();
    this.tokenBroadcastSubscription$.complete();
    this.tokenBroadcastSubscription$ = new Subject<void>();
  }

  /**
   * Ensure token listener is active
   * Called when first widget registers
   *
   * Does NOT register with socket - just starts listening
   * ONLY runs when widgets exist - zero overhead on empty dashboard
   */
  private ensureTokenListenerActive(): void {
    if (this.isTokenListenerActive) {
      return;
    }

    if (!this.plugin.enabled) {
      console.warn('[WMS Chart Loader] Cannot start token listener - plugin disabled');
      return;
    }

    console.log('[WMS Chart Loader] Starting token broadcast listener');

    this.listenForTokenUpdates();
    this.isTokenListenerActive = true;
  }

  /**
   * Stop token listener when no widgets need it
   * Called automatically when last widget unregisters (via ngOnDestroy)
   */
  private cleanupTokenListener(): void {
    if (!this.isTokenListenerActive) {
      return;
    }

    if (this.widgetTokenHandlers.size > 0) {
      return;
    }

    console.log('[WMS Chart Loader] Stopping token listener');

    this.stopListeningForTokenUpdates();
    this.isTokenListenerActive = false;
    this.tokenCache = null;
  }

  /**
   * Register widget for token updates
   *
   * 1. Request token ONCE from backend (may return null if not ready)
   * 2. Listen to broadcasts for token updates (always active)
   * 3. Use cached token for subsequent widgets
   *
   * Backend handles:
   * - Initial token fetch
   * - Auto-refresh at 75% lifetime
   * - Retries on failure
   * - Broadcasting to all clients
   */
  async registerNotificationHandler(
    widgetId: string,
    dashboardId: string,
    pluginModule: string,
    handler: (token: string | null) => void
  ): Promise<void> {
    if (this.widgetTokenHandlers.has(widgetId)) {
      return;
    }

    console.info(
      `[WMS Chart Loader] Register token handler for widgetId: ${widgetId} on dashboard: ${dashboardId}`
    );

    // Register widget handler in our local map
    this.widgetTokenHandlers.set(widgetId, handler);

    // Register widget with socket service (triggers connection if first widget)
    this.socket.registerHandler(widgetId, dashboardId);

    // Start token listener (receives broadcasts)
    this.ensureTokenListenerActive();

    // We have a cached token - use it immediately
    if (this.tokenCache !== null) {
      console.log(`[WMS Chart Loader] Using cached token for widget ${widgetId}`);
      handler(this.tokenCache);
      return;
    }

    // No cached token - request from backend once
    // Backend returns token if available, or null if not ready yet
    console.log(`[WMS Chart Loader] Requesting token for widget ${widgetId}`);

    const uuid: string = await this.uuidFromSettings();
    const args = { uuid: uuid };

    try {
      const token = await lastValueFrom(
        this.requestWmsToken$(args).pipe(takeUntil(this.ngUnsubscribe$))
      );

      if (token !== null) {
        console.log(`[WMS Chart Loader] Token received for widget ${widgetId}`);
        this.tokenCache = token;

        // Notify all handlers
        this.widgetTokenHandlers.forEach((h: (token: string | null) => void) => h(token));
      } else {
        // No token yet - backend is still fetching (Keycloak might be starting)
        // Token will arrive via broadcast when backend retries succeed
        console.log(
          `[WMS Chart Loader] Token not ready yet for widget ${widgetId} - ` +
          `waiting for broadcast`
        );
      }
    } catch (error) {
      console.warn(
        `[WMS Chart Loader] Token request failed for widget ${widgetId} - ` +
        `waiting for broadcast`,
        error
      );
    }
  }

  /**
   * Notify backend that the current token is invalid
   *
   * Called by WmsChartServer when tile requests fail with 401/403
   * This closes the feedback loop:
   *   WMS Server rejects tile → Frontend notifies backend → Backend fetches new token
   *   → Backend broadcasts new token → Frontend updates cache → WMS tiles reload
   *
   * Debounced: Multiple simultaneous tile failures only trigger one backend notification
   * (5 second cooldown to avoid spam when many tiles fail at once)
   */
  notifyInvalidToken(): void {
    // Debounce: Multiple tiles failing simultaneously
    if (this.invalidTokenNotificationPending) {
      return;
    }

    this.invalidTokenNotificationPending = true;

    console.warn('[WMS Chart Loader] Token rejected by WMS server - notifying backend');

    // Clear local cache immediately - we know it's invalid
    this.tokenCache = null;

    // Notify all widget handlers that token is invalid
    // Widgets will hide their WMS layers until new token arrives
    this.widgetTokenHandlers.forEach((handler: (token: string | null) => void) => {
      handler(null);
    });

    // Notify backend to immediately fetch a new token
    // Backend will broadcast new token to all clients when ready
    this.socket.emit(WMS_CHART_SERVER_EVENTS.TOKEN_INVALID, {});

    // Reset debounce flag after 5 seconds
    // Prevents rapid re-notifications if token update takes a moment
    setTimeout(() => {
      this.invalidTokenNotificationPending = false;
    }, 5000);
  }

  /**
   * Unregister token handler for widget
   */
  unregisterNotificationHandler(widgetId: string, pluginModule: string): void {
    console.info(`[WMS Chart Loader] Unregister token handler for widgetId: ${widgetId}`);

    this.widgetTokenHandlers.delete(widgetId);
    this.socket.unregisterHandler(widgetId);
    this.cleanupTokenListener();
  }

  /**
   * Send plugin configuration to backend
   */
  transmitConfig$(args: IPlugin): Observable<{ enabled: boolean; error: boolean }> {
    return this.emitAck<{ enabled: boolean; error: boolean }>(PLUGIN_CONFIG_EVENTS.TRANSMIT_CONFIG, args);
  }

  /**
   * Request WMS token from backend
   */
  requestWmsToken$(args: { uuid: string }): Observable<string | null> {
    return this.emitAck<string | null>(WMS_CHART_SERVER_EVENTS.OAUTH_REQUEST, args);
  }

  /**
   * Observe plugin lifecycle events
   */
  private observePluginAction(): void {
    this.modelService._plugin.observePlugin$()
      .pipe(
        filter((data: { plugin: IPlugin, event: 'activate' | 'update' }) =>
          data.plugin.name === this.plugin.name
        ),
        takeUntil(this.ngUnsubscribe$),
        finalize(() => console.log('[WMS Chart Loader] ObservePlugin$ completed'))
      )
      .subscribe(async (data: { plugin: IPlugin, event: 'activate' | 'update' }) => {
        // Plugin has been activated
        if (data.event === 'activate') {
          this.modelService._plugin.getById(data.plugin._id)
            .then((plugin: IPlugin | {}) => {
              if ('_id' in plugin && 'settings' in plugin && plugin.enabled) {
                const settings = <IPluginSettingsExtended>plugin.settings;
                this.plugin.enabled = data.plugin.enabled;

                // Notify socket service that plugin is enabled
                this.socket.setPluginEnabled(data.plugin.enabled);

                if (this.areSettingsValid(settings)) {
                  const user = this.modelService._user.getUser();
                  this.addAccessRestrictions(user);
                }
              }
            });
        }

        // Plugin has been updated
        if (data.event === 'update') {
          if ('settings' in data.plugin && data.plugin.enabled) {
            this.plugin.enabled = data.plugin.enabled;
            this.socket.setPluginEnabled(data.plugin.enabled);

            if (this.widgetTokenHandlers.size > 0 && !this.isTokenListenerActive) {
              this.ensureTokenListenerActive();
            }

            const user = this.modelService._user.getUser();
            this.addAccessRestrictions(user);
          } else if ('enabled' in data.plugin && !data.plugin.enabled) {
            this.plugin.enabled = false;
            this.socket.setPluginEnabled(false);

            if (this.isTokenListenerActive) {
              this.stopListeningForTokenUpdates();
              this.isTokenListenerActive = false;
            }

            this.tokenCache = null;
          }

          // Wait for existing socket connection
          const isConnected = await this.waitForOpenConnection(this.socket);
          if (isConnected) {
            this.transmitConfig$(data.plugin)
              .pipe(takeUntil(this.ngUnsubscribe$))
              .subscribe((result: { enabled: boolean; error: boolean }) => {
                this.handleTransmitResult(result);
              });
          }
        }
      });
  }

  /**
   * Wait for socket connection to be established
   */
  private waitForOpenConnection(socket: WmsChartSocketIoService): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const maxNumberOfAttempts = 10;
      const intervalTime = 200; // ms

      let currentAttempt = 0;
      const interval = setInterval(() => {
        if (currentAttempt > maxNumberOfAttempts - 1) {
          clearInterval(interval);
          resolve(false);
        } else if (socket.ioSocket.connected) {
          clearInterval(interval);
          resolve(true);
        }
        currentAttempt++;
      }, intervalTime);
    });
  }

  /**
   * Set socket authentication token
   */
  private addAccessRestrictions(user: IUser): void {
    const tokenPayload: ISocketTokenAuth = {
      permissionRef: PERMISSION_REF_WMS_CHART_CONSUMER,
      userId: user._id
    };
    const token = btoa(JSON.stringify(tokenPayload));

    // Delegate auth to socket service
    this.socket.setAuthToken(token);
  }

  /**
   * Validate plugin settings
   */
  private areSettingsValid(settings: IPluginSettingsExtended): boolean {
    return settings.auth_url !== '' &&
      settings.client_id !== '' &&
      settings.client_secret !== '' &&
      settings.realm !== '' &&
      settings.wms_server_url !== '';
  }

  /**
   * Generate UUID from plugin settings
   * Used to identify tenant in backend
   */
  async uuidFromSettings(): Promise<string> {
    const registered = this.pluginInjectorService.getPluginByName(this.plugin.name);
    if (registered) {
      const plugin = await this.modelService._plugin.getById(registered._id);
      if ('_id' in plugin) {
        return this.generateUUID(plugin.settings as IPluginSettingsExtended);
      }
    }

    return this.generateUUID(this.plugin.settings);
  }

  /**
   * Generate UUID from tenant settings
   */
  private generateUUID(tenant: IPluginSettingsExtended): string {
    const dataToHash = `${tenant.auth_url}${tenant.realm}${tenant.client_id}${tenant.client_secret}${tenant.wms_server_url}`;
    return uuidv5(dataToHash, uuidv5.URL);
  }

  /**
   * Show snackbar message
   */
  private getMessage(message: string, panelClass: string): void {
    this.snackBar.open(message, '', {
      duration: 2000,
      panelClass: panelClass,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
  }

  /**
   * Handle plugin configuration result
   */
  private handleTransmitResult(result: { enabled: boolean; error: boolean }): void {
    if (result.error) {
      this.getMessage(this.translator.instant('WMS_CHART_LOADER.CONNECT_ERROR_MESSAGE'), 'error-snackbar');
    } else if (result.enabled) {
      this.getMessage(this.translator.instant('WMS_CHART_LOADER.CONNECT_SUCCESS_MESSAGE'), 'success-snackbar');
    } else {
      this.getMessage(this.translator.instant('WMS_CHART_LOADER.DISCONNECT_SUCCESS_MESSAGE'), 'success-snackbar');

      // Clear token cache when plugin is disabled
      this.tokenCache = null;
    }
  }

  /**
   * Start service-wide subscriptions
   */
  start(): void {
    this.resetSubDestroySignal();
    this.observePluginAction();
  }

  /**
   * Reset unsubscribe signal if needed
   */
  private resetSubDestroySignal(): void {
    if (this.ngUnsubscribe$.closed) {
      this.ngUnsubscribe$ = new Subject<void>();
    }
  }

  /**
   * Stop service-wide subscriptions and cleanup
   */
  stop(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();

    // Stop listening for token broadcasts
    this.stopListeningForTokenUpdates();
    this.widgetTokenHandlers.clear();
    this.tokenCache = null;
    this.isTokenListenerActive = false;
    this.invalidTokenNotificationPending = false;
  }
}
